import React, { useState, useEffect, useMemo } from 'react';
import {
  TextField, Paper, Box, FormControlLabel, Switch,
  MenuItem, Select, InputLabel, FormControl, Typography, CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../reduxStore/Store';
import { createOrUpdateUser, deleteUser, setSelectedUser } from '../../reduxStore/UserSlice';
import { fetchRoles } from '../../reduxStore/RoleSlice';
import { useSnackbar } from '../SnackbarProvider';
import MyButton from '../MyButton';
import { ButtonType } from '../../constants/enums';
import api from '../../reduxStore/api';

interface UtilisateurFormProps {
  onCancel: () => void;
}

const UtilisateurForm: React.FC<UtilisateurFormProps> = ({ onCancel }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const { selectedUser } = useSelector((state: any) => state.UserSlice);
  const { roleData, loading: rolesLoading } = useSelector((state: RootState) => state.RoleSlice);
  const { permissions } = useSelector((state: RootState) => state.AuthSlice);
  // Attribuer un rôle = donner des droits : réservé à roles.update (vérifié aussi par l'API).
  const peutAttribuerRole = permissions.includes('roles.read') && permissions.includes('roles.update');

  const isEdit = !!selectedUser;

  const initialFormData = useMemo(() => ({
    id: -1,
    login: '',
    password: '',
    email: '',
    phone: '',
    is_active: 1,
    role_id: '' as number | '',
  }), []);

  const [formData, setFormData] = useState(initialFormData);
  const [changePassword, setChangePassword] = useState(false);

  // Chargement des rôles RBAC disponibles
  useEffect(() => {
    if (peutAttribuerRole) dispatch(fetchRoles({ limit: 500, offset: 0 }));
  }, [dispatch, peutAttribuerRole]);

  // Pré-remplissage et pré-sélection du rôle courant en édition
  useEffect(() => {
    if (selectedUser) {
      setFormData({
        id:        selectedUser.id,
        login:     selectedUser.login,
        password:  '',
        email:     selectedUser.email,
        phone:     selectedUser.phone,
        is_active: selectedUser.is_active,
        role_id:   '',
      });
      setChangePassword(false);
      // Récupère le rôle RBAC actuel de l'utilisateur
      if (peutAttribuerRole) {
        api.get(`/roles/user/${selectedUser.id}`).then(r => {
          const roles = r.data.data || [];
          if (roles.length > 0) {
            setFormData(prev => ({ ...prev, role_id: roles[0].id }));
          }
        });
      }
    } else {
      setFormData(initialFormData);
      setChangePassword(false);
    }
  }, [initialFormData, selectedUser, peutAttribuerRole]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.login.trim()) {
      showSnackbar('Le login est obligatoire.', 'error');
      return;
    }
    if (!formData.email.trim()) {
      showSnackbar("L'email est obligatoire.", 'error');
      return;
    }
    if (!isEdit && !formData.password) {
      showSnackbar('Le mot de passe est obligatoire à la création.', 'error');
      return;
    }
    if (isEdit && changePassword && !formData.password) {
      showSnackbar('Saisissez le nouveau mot de passe.', 'error');
      return;
    }

    const payload: any = {
      id:        formData.id,
      login:     formData.login,
      email:     formData.email,
      phone:     formData.phone,
      is_active: formData.is_active,
    };
    if (!isEdit || changePassword) {
      payload.password = formData.password;
    }
    if (peutAttribuerRole && formData.role_id !== '') {
      payload.role_id = formData.role_id;
    }

    try {
      await dispatch(createOrUpdateUser(payload)).unwrap();
      showSnackbar(isEdit ? 'Utilisateur modifié !' : 'Utilisateur créé !', 'success');
      dispatch(setSelectedUser(null));
      setFormData(initialFormData);
      onCancel();
    } catch (error) {
      showSnackbar((error as string) || "Échec de l'enregistrement.", 'error');
    }
  };

  const handleDelete = async () => {
    if (!selectedUser?.id) return;
    if (!window.confirm(`Supprimer l'utilisateur "${selectedUser.login}" ?`)) return;
    try {
      await dispatch(deleteUser(selectedUser.id)).unwrap();
      showSnackbar('Utilisateur supprimé !', 'success');
      dispatch(setSelectedUser(null));
      onCancel();
    } catch (error) {
      showSnackbar((error as string) || 'Échec de la suppression.', 'error');
    }
  };

  return (
    <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
      <Box display="grid" gridTemplateColumns="repeat(1, 1fr)" gap={2}>
        <TextField
          name="login"
          label="Login"
          value={formData.login}
          onChange={handleChange}
          fullWidth
          size="small"
          required
          disabled={isEdit}
          helperText={isEdit ? 'Le login ne peut pas être modifié.' : ''}
        />
        <TextField
          name="email"
          label="Email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          fullWidth
          size="small"
          required
        />
        <TextField
          name="phone"
          label="Téléphone"
          value={formData.phone}
          onChange={handleChange}
          fullWidth
          size="small"
        />

        {peutAttribuerRole && (
        <FormControl size="small" fullWidth>
          <InputLabel>Rôle</InputLabel>
          <Select
            value={formData.role_id}
            label="Rôle"
            onChange={(e) => setFormData(prev => ({ ...prev, role_id: e.target.value as number }))}
            endAdornment={rolesLoading ? <CircularProgress size={16} sx={{ mr: 2 }} /> : null}
          >
            {roleData.map(r => (
              <MenuItem key={r.id} value={r.id}>{r.nom}</MenuItem>
            ))}
          </Select>
        </FormControl>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={formData.is_active === 1}
              onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked ? 1 : 0 }))}
            />
          }
          label="Compte actif"
        />

        {isEdit && (
          <FormControlLabel
            control={
              <Switch
                checked={changePassword}
                onChange={(e) => setChangePassword(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Changer le mot de passe</Typography>}
          />
        )}

        {(!isEdit || changePassword) && (
          <TextField
            name="password"
            label="Mot de passe"
            type="password"
            value={formData.password}
            onChange={handleChange}
            fullWidth
            size="small"
            required={!isEdit}
          />
        )}
      </Box>

      <Box mt={2} display="flex" gap={1}>
        <MyButton actionType={ButtonType.INSERT} variant="contained" onClick={handleSubmit}>
          {isEdit ? 'Modifier' : 'Ajouter'}
        </MyButton>
        {isEdit && (
          <MyButton actionType={ButtonType.DELETE} variant="outlined" onClick={handleDelete}>
            Supprimer
          </MyButton>
        )}
        <MyButton actionType={ButtonType.CANCEL} variant="outlined" onClick={onCancel}>
          Annuler
        </MyButton>
      </Box>
    </Paper>
  );
};

export default UtilisateurForm;
